import Cookies from 'js-cookie';
import { Dictionary, isEmpty } from 'lodash';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { useAddresses } from '@/hooks/useAddresses';

import { resetAuthInterceptorState } from '@/components/shared/ManageTokenForClient/tokenManager';
import {
  clearTokenRefresher,
  initTokenRefresher,
  setGlobalLogoutHandler,
} from '@/components/shared/ManageTokenForClient/tokenRefresher';

import { getGlobalCountry } from '@/atoms/countryState';
import { modalOpenErrorAtom } from '@/atoms/sync-amazon-order/modalOpenError';
import { successFullModalAtom } from '@/atoms/sync-amazon-order/successFullModalAtom';
import { syncAmazonOrderId } from '@/services/book-claim';
import { flushAndHydrateGuestEvents } from '@/services/Events/eventBulkProcessor';
import { clearSessionStateCookie } from '@/utils/getSessionStateFromCookie';
import { addLocalPrefix, getAllByPrefix } from '@/utils/LocalStorage';
import { programmaticScrollTo } from '@/utils/ScrollControl/scrollControl';

import { EventDetails, logEvent } from './useClickEventTracking';
import { authModalControl, tokenUpdate } from '../atoms/authModalControl';
import { cartAtom, checkCouponAtom } from '../atoms/Cart';
import { localCart } from '../atoms/LocalCart';
import { localUserID, user } from '../atoms/user';
import { extractSlugAndProductId } from '../components/BookLandingPageComp/SlidBookContainer';
import { formatCartData } from '../components/CartPage';
import {
  clearSession,
  setSessionValid,
} from '../components/shared/AppWriteLocalStroage';
import { notify } from '../components/shared/basic/notify';
import {
  formateErrorAppwriteObject,
  formateErrorObject,
} from '../components/shared/formateErrorObject';
import {
  getLoginToken,
  getUserId,
  logout,
  setToken,
  setUserId,
  setUserName,
  setZl_UID,
  storeTokenExpiration,
} from '../components/shared/getToken';
import {
  trackCampaignSignUp,
  trackLoginEvent,
  trackSignupEvent,
} from '../components/shared/Gtags';
import { resetSessionExistsCookie } from '../components/shared/ManageTokenForClient/manageCookies';
import appwriteService, { setTokenInCookies } from '../config/appwrite';
import { Endpoints } from '../network';
import { affiliateCodeSend } from '../services/Affiliate';
import { apiClient } from '../services/apiClients';
import { getCartCount, handleCheckCoupon } from '../services/Cart';
import {
  deleteFirebaseToken,
  deleteFirebaseTokenNotification,
} from '../services/Firebase';
import { getSyncBookListApi } from '../services/Library/Books';
import { liveRegistrationAlreadyRegistered } from '../services/Live';
import { subscribeForUpcomingBooks } from '../services/Products';
import { getEventContext } from '../utils/eventContext';
import useFirebaseNotifications from '../utils/firebase/firebaseSetup';
import { AppwriteUserData, User } from '../utils/interfaces/User';

interface PostHogUserProps {
  id: string;
  email?: string;
  name?: string;
  [key: string]: any;
}

interface LoginOptions {
  email: string;
  password: string;
  login_by: string;
  redirectUrl?: string;
  isModal?: boolean;
  onCancel?: () => void;
  noRedirection?: boolean;
  setEmailExistAndPassword?: any;
  bookId?: string;
  isIFrame?: boolean;
}

export interface SignUpOptions {
  email: string;
  password: string;
  name: string;
  phone?: string;
  isModal?: boolean;
  onCancel?: () => void;
  noRedirect?: boolean;
  isRegister?: boolean;
  liveSlug?: string;
  registerClose?: () => void;
  phoneCountryCode?: string;
  phonePrefix?: string;
}

export const useAuth = () => {
  // const token = getLoginToken();
  const [loading, setLoading] = useState<boolean>(false);
  const [visible, setVisible] = useRecoilState(authModalControl);
  const [, setIsTokenChanged] = useRecoilState(tokenUpdate);

  const [userData, setUserData] = useRecoilState<User | undefined>(user);
  const [, setSuccessFullModalOpen] = useRecoilState(successFullModalAtom);

  const localUserId = useRecoilValue(localUserID);
  const [cartData, setCartData] = useRecoilState(cartAtom);
  const [, setLocalCartCount] = useRecoilState(localCart);
  const [, setCouponData] = useRecoilState(checkCouponAtom);
  const { deleteFCMToken } = useFirebaseNotifications();
  const [, setModalOpen] = useRecoilState(modalOpenErrorAtom);

  const { resetAddressesCache } = useAddresses();

  const syncInFlightRef = useRef(false);
  const lastSyncedKeyRef = useRef<string | null>(null);

  const router = useRouter();
  const {
    bookId: book_id,
    postId,
    schoolId,
    watchId,
    slug,
    school_id,
    workshop_id,
    post_id,
    cartId,
    id: courseId,
  } = router.query;

  const productBookId = extractSlugAndProductId(router.asPath);

  const excludedPaths = [
    '/cart',
    '/personality_test',
    '/reader-personality-test',
    '/checkout',
    '/subscription',
    '/plans',
    '/affiliate',
    '/live/[slug]',
    '/live/watch/[watchId]',
    '/live',
    '/claim-book',
    '/byob',
    '/organization-join',
    '/corporate',
  ];

  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));
  const handleCartDetails = async (userId: string) => {
    try {
      const res = await apiClient.post(Endpoints.getCartDetails, {
        user_id: userId,
      });

      const cartId = res?.data?.cart_id;

      const formattedData = formatCartData(
        res?.data?.data?.[0]?.cart_items,
        '',
        cartId,
      );

      setCartData(formattedData);
      await delay(500);

      await handleCartCount(userId);
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
    }
  };

  const handleCartCount = async (userId: string) => {
    try {
      const data = await getCartCount({ id: userId });
      // setUserData(data?.data);
      setLocalCartCount(data.count);
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    }
  };

  const identifyUserWithPostHog = (user: PostHogUserProps) => {
    if (typeof window === 'undefined' || !window.posthog || !user?.id) return;

    const alreadyIdentified = window.posthog.get_property?.('$user_id');

    if (alreadyIdentified === user.id) return;

    window.posthog.identify?.(user.id, {
      email: user.email,
      name: user.name,
      ...user,
    });

    localStorage.setItem('posthog_identified_user', user.id);
  };

  const onLogin = async (
    options: LoginOptions,
  ): Promise<Dictionary<string | JSX.Element>> => {
    const {
      email,
      password,
      // login_by,
      redirectUrl,
      isModal,
      onCancel,
      noRedirection = false,
      setEmailExistAndPassword,
      bookId,
      isIFrame,
    } = options;

    setLoading(true);
    const errorMessages: Dictionary<JSX.Element | string> = {};

    let notificationSent = false;

    try {
      await appwriteService.logoutIfSessionExists();
      const response = await appwriteService.login({
        email,
        password,
      });
      setSessionValid(true);
      storeTokenExpiration();
      initTokenRefresher();

      if (!isIFrame) {
        const token = await setTokenInCookies();
        let currentUser: AppwriteUserData | undefined = undefined;

        if (response) {
          const user = await appwriteService.getCurrentUser();
          if (user !== null) {
            currentUser = user as unknown as AppwriteUserData;
          }
        }
        const dbUser: User = await appWriteUser(currentUser?.userId as string);

        await setToken(token as string);
        await setUserName(dbUser?.name);
        setUserData(dbUser);
        setIsTokenChanged(true);
        await setUserId(dbUser?.id?.toString());
        await setZl_UID(dbUser?.zl_uid?.toString() as string);

        if (visible.comingSoon) {
          await handleSubscribe(visible.comingSoon);
        }

        if (dbUser?.id?.toString()) {
          trackLoginEvent({
            user_id: dbUser?.id?.toString(),
            email: dbUser?.email,
            name: dbUser?.name,
            phone: dbUser?.phone,
          });
        }

        identifyUserWithPostHog({
          id: dbUser?.zl_uid as string,
          email: dbUser?.email,
          name: dbUser?.name,
        });

        const eventParams = constructEventParams({
          router,
          dbUser,
          book_id: book_id as string,
          schoolId: schoolId as string,
          postId: postId as string,
          slug: slug as string,
          watchId: watchId as string,
          eventName: 'login',
        });

        if (visible.amazonOrderId) {
          handleSyncAmazonOrderId(visible.amazonOrderId);
        }

        logEvent({
          eventDetails: eventParams,
          pathname: router.pathname,
        });
        if (dbUser?.zl_uid) {
          flushAndHydrateGuestEvents({
            zl_uid: dbUser.zl_uid.toString(),
            userId: dbUser?.id?.toString(),
          });
        }
        await getLoginToken();
        getSyncBookListApi();

        if (localUserId) {
          await syncCart(
            dbUser?.id?.toString(),
            (cartData[0]?.cartId as number) || Number(cartId),
          );
          const checkData = await handleCheckCoupon(
            (cartData[0]?.cartId as number) || Number(cartId),
          );
          if (typeof checkData?.data !== undefined) {
            setCouponData(checkData.data);
          }
        }
        if (setEmailExistAndPassword) {
          setEmailExistAndPassword((prevErrors: any) => ({
            ...prevErrors,
            emailExist: false,
          }));
        }
        const onboardingCompleted = dbUser?.user_attributes?.some(
          (attr) =>
            attr.attribute_key === 'onboarding_completed' &&
            attr.attribute_value === '1',
        );

        const currentPath = router.pathname;
        const isExcluded = excludedPaths.some((path) =>
          currentPath.startsWith(path) || currentPath.includes(path),
        );

        if (!onboardingCompleted && !isExcluded) {
          router.push('/');
        } else {
          if (!noRedirection && !visible?.noRedirect) {
            if (!isModal) {
              await router.push(redirectUrl ?? '/');
            } else {
              onCancel && onCancel();
            }
          }
        }

        if (isEmpty(dbUser)) {
          throw Error();
        }

        if (!notificationSent) {
          if (bookId) {
            router.push(`/pdf/${bookId}?samplePdf=true`);
          }
          setVisible((prevState) => ({
            ...prevState,
            login: false,
            signUp: false,
            bookId: '',
            comingSoon: '',
          }));
          // notify('Successfully Logged IN!', 'success');
          notificationSent = true;
        }
      } else {
        // await setAuthToken(data?.data?.access_token);
        router.reload();
        setIsTokenChanged(true);
      }
      resetSessionExistsCookie();
      clearSession();
    } catch (error: any) {
      const message = formateErrorAppwriteObject(error);

      notify(message, 'error');

      if (setEmailExistAndPassword) {
        setEmailExistAndPassword((prevErrors: any) => ({
          ...prevErrors,
          emailExist: true,
        }));
      }
      throw error;
    } finally {
      setLoading(false);
    }

    return errorMessages;
  };

  const resolveCartId = (
    stateCartId?: number,
    propCartId?: number,
    queryCartId?: string | string[],
  ): number | undefined => {
    if (stateCartId) return stateCartId;
    if (propCartId) return propCartId;

    const qId = Array.isArray(queryCartId) ? queryCartId[0] : queryCartId;
    return qId ? Number(qId) : undefined;
  };

  const syncCart = useCallback(
    async (user_id: string, propCartId?: number) => {
      if (syncInFlightRef.current) return;
      syncInFlightRef.current = true;

      try {
        const userIdCookie = await getUserId();
        const finalUserId = user_id || userIdCookie;

        if (!finalUserId || !localUserId) return;

        const resolvedCartId = resolveCartId(
          cartData?.[0]?.cartId as number,
          propCartId,
          router.query.cartId,
        );

        if (!resolvedCartId) return;

        const syncKey = `${finalUserId}_${resolvedCartId}`;

        if (lastSyncedKeyRef.current === syncKey) return;

        const payload = {
          temp_user_id: localUserId,
          user_id: finalUserId,
          cart_id: resolvedCartId,
        };

        await apiClient.post(Endpoints.syncCart, payload);

        lastSyncedKeyRef.current = syncKey;

        await handleCartDetails(finalUserId);
      } catch (error) {
        if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
          console.error('syncCart error:', error);
        }
        throw error;
      } finally {
        syncInFlightRef.current = false;
      }
    },
    [localUserId, cartData?.[0]?.cartId, router.query.cartId],
  );

  const handleDeviceToken = async (
    device_token_id: string | undefined,
  ): Promise<boolean> => {
    try {
      if (device_token_id) {
        await deleteFirebaseTokenNotification();
        await deleteFirebaseToken(device_token_id);
      }

      const eventParams: EventDetails = {
        event_name: 'logout',
        source: 'global',
        source_type: 'global',
        source_id: null,
      };

      await logEvent({
        eventDetails: eventParams,
        userData,
        pathname: router.pathname,
      });

      return true;
    } catch (error: any) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
      return true;
    }
  };

  const performLogout = async () => {
    setUserData(undefined);
    setCartData([]);
    localStorage.removeItem('auth_token_expiration');
    clearTokenRefresher();
    const channel = new BroadcastChannel('auth');

    channel.postMessage({ type: 'logout' });

    Cookies.remove('user_id');
    Cookies.remove('auth_token');
    Cookies.remove('userName');
    Cookies.remove('book_token');
    Cookies.remove('device_token_id');
    Cookies.remove('device_id');

    try {
      await appwriteService.logout();
      deleteFCMToken();
      if (router.pathname === '/cart' && router.query.cartId) {
        const { cartId, ...restQuery } = router.query;

        router.replace(
          {
            pathname: '/cart',
            query: restQuery,
          },
          undefined,
          { shallow: true },
        );
      }
      logout();
      router.reload();
      notify('Logged out successfully', 'success');
    } catch (error: any) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    } finally {
      clearSession();
      resetSessionExistsCookie();
      setLoading(false);
    }
  };

  const onLogOut = async (): Promise<Dictionary<string | JSX.Element>> => {
    const errorMessages: Dictionary<string | JSX.Element> = {};
    const device_token_id = Cookies.get('device_token_id');

    try {
      await handleDeviceToken(device_token_id);

      await performLogout();
    } finally {
      resetAuthInterceptorState();
      clearSessionStateCookie();
      resetAddressesCache();

      Cookies.remove('auth_token');
      Cookies.remove('user_id');
      localStorage.removeItem('auth_token_expiration');
    }

    return errorMessages;
  };

  const handleSubscribe = async (productId: string) => {
    try {
      if (productId) {
        await subscribeForUpcomingBooks(productId);
      }
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    }
  };
  useEffect(() => {
    setGlobalLogoutHandler(onLogOut);
    return () => setGlobalLogoutHandler(null);
  }, [onLogOut]);

  const constructEventParams = ({
    router,
    dbUser,
    postId,
    eventName,
    sso,
  }: {
    router: any;
    dbUser: any;
    book_id?: string;
    schoolId?: string;
    postId?: string;
    slug?: string;
    watchId?: string;
    eventName: string;
    sso?: string;
  }): EventDetails => {
    const eventCtx = getEventContext();
    const path = router.pathname;

    // Source is determined by EXACT Next.js route template matching.
    // router.pathname returns the template (e.g. '/books/[bookId]'), not the real URL.
    const getSource = (): string => {
      if (path === '/books/[bookId]') return 'books';
      if (path === '/bundles/[bookId]') return 'bundles';
      if (path === '/courses/[courses_slug]') return 'courses';
      if (path === '/live/[slug]' || path === '/live/watch/[watchId]')
        return 'live';
      if (path === '/schools/[schoolId]/[postId]') return 'schools';
      if (path.startsWith('/cart') || path === '/checkout-v2')
        return 'checkout';
      return 'global';
    };

    const source = getSource();

    const eventParams: EventDetails = {
      event_name: eventName,
      source: source === 'checkout' ? 'global' : source,
      source_type: source === 'live' ? 'workshop' : source,
      source_id: null,
      sub_source: null,
      sub_source_id: null,
      zl_uid: dbUser?.zl_uid,
      source_name: source !== 'global' ? eventCtx?.source_name || null : null,
      meta: {
        ...(source !== 'global' && eventCtx?.product_category
          ? { product_category: eventCtx.product_category.toLocaleLowerCase() }
          : {}),
      },
    };

    if (sso) {
      Object.assign(eventParams, { meta: { sso } });
    }

    // Enrich source_type / source_id — use exact templates to stay in sync with getSource()
    if (path === '/books/[bookId]') {
      const isShort = router.query?.short === 'true';
      Object.assign(eventParams, {
        source_type: isShort ? 'short' : 'long',
        source_id: productBookId as string,
      });
    } else if (path === '/bundles/[bookId]') {
      Object.assign(eventParams, {
        source_type: 'bundles',
        source_id: productBookId,
      });
    } else if (path === '/courses/[courses_slug]') {
      Object.assign(eventParams, {
        source_id: courseId as string,
      });
    } else if (path === '/schools/[schoolId]/[postId]') {
      Object.assign(eventParams, {
        source_type: 'schools',
        source_id: school_id as string,
        source_name: eventCtx?.source_name || null,
      });
      if (postId) {
        Object.assign(eventParams, {
          sub_source: 'chapter',
          sub_source_id: post_id as string,
          sub_source_name: eventCtx?.sub_source_name || null,
          source_type: 'schools',
        });
      }
    } else if (path === '/live/[slug]' || path === '/live/watch/[watchId]') {
      Object.assign(eventParams, {
        source_id: workshop_id as string,
      });
    }

    return eventParams;
  };

  const tryUpdatePhoneSafely = async ({
    formattedPhone,
    password,
  }: {
    formattedPhone: string;
    password: string;
  }): Promise<void> => {
    try {
      await appwriteService.updatePhone(formattedPhone, password);
    } catch (phoneUpdateError) {
      console.error(
        'Phone update failed, continuing signup flow:',
        phoneUpdateError,
      );
    }
  };

  const handleLiveRegistration = async ({
    dbUser,
    phone,
    liveSlug,
    phonePrefix,
  }: {
    dbUser: User;
    phone: string;
    phonePrefix: string;
    liveSlug: string;
  }) => {
    try {
      await liveRegistrationAlreadyRegistered({
        email: dbUser?.email as string,
        live_workshop_slug: liveSlug,
        name: dbUser?.name as string,
        ecom_user_id: dbUser?.id as number,
        phone: phone as string,
        country_code: phonePrefix,
      });
    } catch (error) {
      console.error('Live registration failed:', error);
    }
  };

  const handleSyncAmazonOrderId = async (id: string) => {
    try {
      await syncAmazonOrderId(id);
      setSuccessFullModalOpen(true);
    } catch (error: any) {
      if (error?.status === 400) setModalOpen(true);
      notify(formateErrorObject(error), 'error');
    }
  };

  const onSignUp = async (
    options: SignUpOptions,
  ): Promise<Dictionary<string | JSX.Element>> => {
    const {
      email,
      password,
      name,
      isModal,
      onCancel,
      noRedirect,
      isRegister,
      registerClose,
      liveSlug,
      phoneCountryCode,
      phonePrefix,
      phone,
    } = options;

    setLoading(true);
    const errorMessages: Dictionary<JSX.Element | string> = {};
    let notificationSent = false;
    let dbUser: User | undefined = undefined;
    let token: string | undefined;
    let signupSuccessful = false;

    try {
      await appwriteService.logoutIfSessionExists();
      await appwriteService.createUserAccount({ email, password, name });
      await appwriteService.logoutIfSessionExists();
      const userLogin = await appwriteService.login({ email, password });
      token = await setTokenInCookies();

      let currentUser: AppwriteUserData | undefined;

      if (userLogin) {
        const user = await appwriteService.getCurrentUser();
        if (user !== null) {
          currentUser = user as unknown as AppwriteUserData;

          const isPhoneMissing =
            !currentUser.phone || currentUser.phone.trim() === '';
          const formattedPhone =
            phone && phonePrefix ? `+${phonePrefix}${phone}` : null;

          if (isPhoneMissing && formattedPhone) {
            await appwriteService.updateOnboardingPrefs({
              country_code: phonePrefix,
              iso2: phoneCountryCode,
            });

            await tryUpdatePhoneSafely({
              formattedPhone,
              password,
            });

            const updatedUser = await appwriteService.getCurrentUser();
            currentUser = updatedUser as unknown as AppwriteUserData;
          }
        }
      }

      dbUser = await appWriteUser(currentUser?.userId as string);

      signupSuccessful = true;

      if (isRegister && liveSlug) {
        await handleLiveRegistration({
          dbUser: dbUser as User,
          phone: phone || '',
          phonePrefix: phonePrefix || '',
          liveSlug: liveSlug || '',
        });
      }

      identifyUserWithPostHog({
        id: dbUser?.zl_uid as string,
        email: dbUser?.email,
        name: dbUser?.name,
      });

      const onboardingCompleted = dbUser?.user_attributes?.some(
        (attr) =>
          attr.attribute_key === 'onboarding_completed' &&
          attr.attribute_value === '1',
      );

      const currentPath = router.pathname;
      const isExcluded = excludedPaths.some((path) =>
        currentPath.startsWith(path) || currentPath.includes(path),
      );

      if (!onboardingCompleted && !isExcluded) {
        router.push('/');
      } else {
        if (!noRedirect && !visible?.noRedirect) {
          if (isModal === false && !router.pathname.includes('/cart')) {
            if (visible.bookId) {
              await router.push(`/pdf/${visible.bookId}?samplePdf=true`);
            } else {
              await router.push('/');
            }
          } else {
            onCancel && onCancel();
          }
        }
      }

      if (visible.comingSoon) {
        handleSubscribe(visible.comingSoon);
      }

      if (book_id || postId) {
        await trackCampaignSignUp({
          email,
          name,
          ...(book_id && { origin: 'book', slug: book_id as string }),
          ...(postId && { origin: 'school', slug: postId as string }),
        });
      }

      if (isRegister && registerClose) registerClose();
      if (isRegister && router.asPath.includes('/live')) {
        setTimeout(() => {
          programmaticScrollTo({ top: 0, behavior: 'smooth' });
        }, 300);
      }
      if (isRegister && router.asPath.includes('/live')) {
        setTimeout(() => {
          programmaticScrollTo({ top: 0, behavior: 'smooth' });
        }, 300);
      }

      if (visible.amazonOrderId) {
        handleSyncAmazonOrderId(visible.amazonOrderId);
      }

      if (!notificationSent) {
        setVisible((prevState) => ({
          ...prevState,
          signUp: false,
          login: false,
          comingSoon: '',
        }));
        notify('Successfully Signed Up!', 'success');
        notificationSent = true;
      }

      resetSessionExistsCookie();
      clearSession();
      if (getGlobalCountry().timezone) {
        await appwriteService.updateOnboardingPrefs({
          sourcePlatform: 'website',
          timezone: getGlobalCountry().timezone as string,
        });
      } else {
        await appwriteService.updateOnboardingPrefs({
          sourcePlatform: 'website',
        });
      }

      await handlePostSignUpData({ dbUser: dbUser as User, token });

      if (dbUser?.id?.toString()) {
        trackSignupEvent({
          email,
          name,
          password,
          user_id: dbUser?.id?.toString(),
        });
      }
    } catch (error: any) {
      const message = formateErrorAppwriteObject(error);
      notify(message, 'error');

      if (
        error.code === 400 &&
        error.type === 'general_bad_request' &&
        (router.asPath.includes('/cart') || router.asPath.includes('/checkout'))
      ) {
        setVisible((prevState) => ({
          ...prevState,
          signUp: false,
          login: true,
          comingSoon: '',
        }));
      }

      notificationSent = true;
      throw error;
    } finally {
      const eventParams = constructEventParams({
        router,
        dbUser: dbUser ?? null,
        book_id: book_id as string,
        schoolId: schoolId as string,
        postId: postId as string,
        slug: slug as string,
        watchId: watchId as string,
        eventName: 'signup',
      });
      if (signupSuccessful) {
        logEvent({ eventDetails: eventParams, pathname: router.pathname });
        if (dbUser?.zl_uid) {
          flushAndHydrateGuestEvents({
            zl_uid: dbUser.zl_uid.toString(),
            userId: dbUser?.id?.toString(),
          });
        }
      }
      setLoading(false);
    }

    return errorMessages;
  };

  const shortSignUp = async (
    email: string,
    name: string,
    phone: string,
  ): Promise<Dictionary<string | JSX.Element>> => {
    setLoading(true);
    const errorMessages: Dictionary<JSX.Element | string> = {};

    let notificationSent = false;

    try {
      const response = await apiClient.post(Endpoints.shortSignUp, {
        email,
        name,
        phone,
        temp_user_id: localUserId,
      });
      const { data } = response;

      if (localUserId) {
        syncCart(
          data?.data?.user?.id,
          (cartData[0]?.cartId as number) || Number(cartId),
        );
      }

      await setToken(data?.data?.access_token?.plainTextToken);
      await setUserName(data?.data?.user?.name);
      setUserData(data?.data?.user);

      await setUserId(data?.data?.user?.id);
      await setZl_UID(data?.data?.user?.zl_uid);
      if (data?.data?.user?.zl_uid) {
        flushAndHydrateGuestEvents({
          zl_uid: data.data.user.zl_uid.toString(),
          userId: data.data.user.id?.toString(),
        });
      }
      if (!notificationSent) {
        notificationSent = true;
      }
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');

      if (!notificationSent) {
        notificationSent = true;
      }
    } finally {
      setLoading(false);
    }
    return errorMessages;
  };

  const handlePostSignUpData = async (response: {
    dbUser: User;
    token: string;
    loginType?: string;
  }) => {
    const isRegisterLogin = response?.token;

    await setUserId(response?.dbUser?.id?.toString());
    await setZl_UID(response?.dbUser?.zl_uid?.toString() as string);
    if (localUserId) {
      if (isRegisterLogin) {
        syncCart(
          response?.dbUser?.id?.toString(),
          (cartData[0]?.cartId as number) || Number(cartId),
        );
      } else {
        syncCart(
          response?.dbUser?.id?.toString(),
          (cartData[0]?.cartId as number) || Number(cartId),
        );
      }
    }
    if (response?.loginType) {
      const { isSignup } = await appwriteService.checkIsSignup();
      if (isSignup) {
        const eventParams = constructEventParams({
          router,
          dbUser: response?.dbUser,
          book_id: book_id as string,
          schoolId: schoolId as string,
          postId: postId as string,
          slug: slug as string,
          watchId: watchId as string,
          eventName: 'signup',
          sso: response?.loginType,
        });

        await appwriteService.updateOnboardingPrefs({
          sourcePlatform: 'website',
        });
        logEvent({
          eventDetails: eventParams,
          pathname: router.pathname,
        });
      } else {
        const eventParams = constructEventParams({
          router,
          dbUser: response?.dbUser,
          book_id: book_id as string,
          schoolId: schoolId as string,
          postId: postId as string,
          slug: slug as string,
          watchId: watchId as string,
          eventName: 'login',
          sso: response?.loginType,
        });

        logEvent({
          eventDetails: eventParams,
          pathname: router.pathname,
        });
      }
    }

    const marketingDataFromLocalStorage = getAllByPrefix([
      'afc_',
      'ref_',
      'utm_',
    ]);
    const marketingDataFromLocalStorageWithLocalPrefix = addLocalPrefix(
      marketingDataFromLocalStorage,
    );
    if (marketingDataFromLocalStorageWithLocalPrefix) {
      const data = marketingDataFromLocalStorageWithLocalPrefix;
      await appwriteService.updateOnboardingPrefs({ data });
    }

    await setToken(response?.token);
    await setUserName(response?.dbUser?.name);

    setUserData(response?.dbUser);
    await setUserId(response?.dbUser?.id?.toString());
    await setZl_UID(response?.dbUser?.zl_uid?.toString() as string);
    if (response?.dbUser?.zl_uid) {
      flushAndHydrateGuestEvents({
        zl_uid: response.dbUser.zl_uid.toString(),
        userId: response.dbUser.id?.toString(),
      });
    }
    const affiliateCode = Cookies.get('afc');
    if (affiliateCode) {
      affiliateCodeSend(affiliateCode);
      Cookies.remove('afc');
    }

    setIsTokenChanged(true);
  };

  return {
    loading,
    onLogin,
    onSignUp,
    onLogOut,
    shortSignUp,
    syncCart,
    handlePostSignUpData,
    identifyUserWithPostHog,
  };
};

export const appWriteUser = async (zl_uid: string) => {
  try {
    const ref_code = Cookies.get('ref_code');

    const requestBody = {
      zl_uid,
      ...(ref_code && { ref_code }),
    };

    const response = await apiClient.post(Endpoints.appWriteUser, requestBody);

    return response?.data?.data;
  } catch (error: any) {
    if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
      console.error('Error initializing messaging:', error);
    }
  }
};

export const setUserPropertiesPostHog = async (
  setProps: Record<string, any> = {},
  setOnceProps: Record<string, any> = {},
) => {
  if (typeof window !== 'undefined' && window.posthog?.setPersonProperties) {
    window.posthog.setPersonProperties(setProps, setOnceProps);
  }
};
